import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="bridge-element-form"
// Generic search + link creation for linking graph nodes via graph edges.
// Searches via the admin search endpoint and creates edges via form submission.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    searchUrl: String,
    createUrl: String,
    frameId: String,
    fromNodeId: { type: Number, default: 0 },
    toNodeId: { type: Number, default: 0 },
    emptyMessage: { type: String, default: "No results found" }
  }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search() {
    const query = this.inputTarget.value.trim()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.debounceTimer = setTimeout(() => this.performSearch(query), 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const items = await response.json()
        this.displayResults(items)
      }
    } catch (error) {
      console.error("Search failed:", error)
    }
  }

  displayResults(items) {
    if (items.length === 0) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-base-content/50">${this.escapeHtml(this.emptyMessageValue)}</div>`
    } else {
      this.resultsTarget.innerHTML = items.map(item =>
        `<div class="p-2 hover:bg-base-200 cursor-pointer border-b border-base-200 last:border-b-0 text-sm"
              data-action="click->bridge-element-form#selectItem"
              data-item-id="${item.id}">
           ${this.escapeHtml(item.label)}
         </div>`
      ).join("")
    }
    this.resultsTarget.classList.remove("hidden")
  }

  selectItem(event) {
    const itemId = event.currentTarget.dataset.itemId
    this.hideResults()
    this.inputTarget.value = ""
    this.createLink(itemId)
  }

  createLink(selectedId) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.createUrlValue
    form.style.display = "none"

    if (this.hasFrameIdValue) {
      form.setAttribute("data-turbo-frame", this.frameIdValue)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const tokenInput = document.createElement("input")
      tokenInput.type = "hidden"
      tokenInput.name = "authenticity_token"
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    const fromInput = document.createElement("input")
    fromInput.type = "hidden"
    fromInput.name = "from_node_id"
    fromInput.value = this.fromNodeIdValue || selectedId
    form.appendChild(fromInput)

    const toInput = document.createElement("input")
    toInput.type = "hidden"
    toInput.name = "to_node_id"
    toInput.value = this.toNodeIdValue || selectedId
    form.appendChild(toInput)

    document.body.appendChild(form)
    form.requestSubmit()
    form.remove()
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
